/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { JSXElementConstructor, useEffect, useRef, useState } from 'react';
import isFunction from 'lodash-es/isFunction';
import mapValues from 'lodash-es/mapValues';
import { ActionCreator, Dispatch, Store } from 'redux';

import ReduxStoreContext from '@rover/react-lib/src/context/ReduxStoreContext';
import {
  ActionTypes,
  EffectTypes,
  ExtractActionPayloadType,
  ExtractEffectPayloadType,
  ExtractEffectReturnType,
} from '@rover/react-lib/src/lib/redux-duck';

import isShallowEqual from '../../../utils/isShallowEqual';

export type ActionProps<A extends ActionTypes> = {
  [Property in keyof A]: (payload: ExtractActionPayloadType<A[Property]>) => void;
};

export type EffectProps<E extends EffectTypes> = {
  [Property in keyof E]: (
    payload: ExtractEffectPayloadType<E[Property]>
  ) => ExtractEffectReturnType<E[Property]>;
};

type RenderPropArgument<
  Actions extends ActionTypes,
  Effects extends EffectTypes,
  Selector extends (...args: any) => any
> = ActionProps<Actions> &
  EffectProps<Effects> &
  ReturnType<Selector> & {
    dispatch: Dispatch<any>;
  };

type RenderProp<
  A extends ActionTypes,
  E extends EffectTypes,
  Selector extends (...args: any) => any
> = (arg: RenderPropArgument<A, E, Selector>) => React.ReactNode;

export type Props<
  Actions extends ActionTypes,
  Effects extends EffectTypes,
  Selector extends (...args: any) => any
> = {
  selector?: Selector;
  actions?: Actions;
  effects?: Effects;
  onMount?: ActionCreator<any> | Array<ActionCreator<any>>;
  onUnmount?: ActionCreator<any> | Array<ActionCreator<any>>;
  store?: Store<any>;
  children: RenderProp<Actions, Effects, Selector>;
};

export type State = {
  selectorProps: Record<string, any>;
  actionProps: Record<string, any>;
  effectProps: Record<string, any>;
  dispatch?: Dispatch<any>;
};

type StoreContextType = {
  reduxStore: Store<any>;
};

type InnerConnectProps<
  A extends ActionTypes,
  E extends EffectTypes,
  S extends (...args: any) => any
> = Props<A, E, S> & StoreContextType;

const getActions = <A extends ActionTypes>(
  actions: A,
  dispatch: Dispatch<any>,
  {
    prevActions,
    prevDispatchActions,
  }: { prevActions: Record<string, any>; prevDispatchActions: Record<string, any> }
): ActionProps<A> =>
  mapValues(actions, (action, name) => {
    if (!action || !isFunction(action)) {
      throw new Error(`Connect action values must be functions (action ${name})`);
    }

    return action === prevActions[name]
      ? prevDispatchActions[name]
      : (...args: any[]) => dispatch(action(...args));
  });

// Memoization cache type for action/effect wrappers
type MemoCache = {
  prevActions: Record<string, any>;
  prevDispatchActions: Record<string, any>;
  prevEffects: Record<string, any>;
  prevDispatchEffects: Record<string, any>;
};

const InnerConnect = ({
  selector = (() => ({})) as any,
  actions = {} as any,
  effects = {} as any,
  store: storeProp,
  reduxStore,
  onMount,
  onUnmount,
  children,
  ...ownProps
}: InnerConnectProps<any, any, any>): JSX.Element => {
  // Determine which store to use
  const store = storeProp || reduxStore;

  // Memoization cache stored in a ref to persist across renders without causing re-renders
  const memoCacheRef = useRef<MemoCache>({
    prevActions: {},
    prevDispatchActions: {},
    prevEffects: {},
    prevDispatchEffects: {},
  });

  // Track unmounted state to prevent updates after unmount
  const unmountedRef = useRef(false);

  // Track if onMount has been dispatched
  // This replaces UNSAFE_componentWillMount behavior - we dispatch during render, not in useEffect
  // IMPORTANT: This must happen BEFORE useState initialization so that synchronous store updates
  // from onMount actions are reflected in the initial selectorProps
  const mountDispatchedRef = useRef(false);
  if (!mountDispatchedRef.current) {
    mountDispatchedRef.current = true;
    if (onMount) {
      if (Array.isArray(onMount)) {
        onMount.forEach((action) => {
          store.dispatch(action());
        });
      } else {
        store.dispatch(onMount());
      }
    }
  }

  // Compute initial selector props synchronously (like UNSAFE_componentWillMount did)
  // This runs AFTER onMount dispatch so synchronous updates are captured
  const getInitialSelectorProps = () => selector(store.getState() || {}, { selector, ...ownProps });

  // State for selector props - initialized synchronously
  const [selectorProps, setSelectorProps] = useState<Record<string, any>>(getInitialSelectorProps);

  // Store the current selectorProps in a ref for comparison in the subscription callback
  const selectorPropsRef = useRef(selectorProps);
  selectorPropsRef.current = selectorProps;

  // Subscribe to store changes
  useEffect(() => {
    // Reset unmounted flag on mount - critical for React Strict Mode double-mounting
    // where the ref persists but the cleanup ran, leaving unmountedRef.current = true
    unmountedRef.current = false;

    const updateState = () => {
      if (unmountedRef.current) {
        return;
      }

      const nextSelectorProps = selector(store.getState() || {}, { selector, ...ownProps });

      if (!isShallowEqual(nextSelectorProps, selectorPropsRef.current)) {
        // Recompute to get fresh value in case state changed during this callback
        const freshSelectorProps = selector(store.getState() || {}, { selector, ...ownProps });
        setSelectorProps(freshSelectorProps);
      }
    };

    const unsubscribe = store.subscribe(updateState);

    // Check if state changed between initial render and subscription setup
    updateState();

    return () => {
      unsubscribe();
    };
    // Props are expected to be static, so we intentionally don't include them in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  // Handle onUnmount - dispatch actions on unmount
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      // Reset mountDispatchedRef so onMount fires again on remount (React Strict Mode)
      mountDispatchedRef.current = false;
      if (onUnmount) {
        if (Array.isArray(onUnmount)) {
          onUnmount.forEach((action) => {
            store.dispatch(action());
          });
        } else {
          store.dispatch(onUnmount());
        }
      }
    };
    // Props are expected to be static, so we intentionally don't include them in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute memoized action wrappers
  const actionProps = (() => {
    const cache = memoCacheRef.current;
    const dispatchActions = getActions(actions, store.dispatch, {
      prevActions: cache.prevActions,
      prevDispatchActions: cache.prevDispatchActions,
    });
    cache.prevActions = actions;
    cache.prevDispatchActions = dispatchActions;
    return dispatchActions;
  })();

  // Compute memoized effect wrappers
  const effectProps = (() => {
    const cache = memoCacheRef.current;
    const dispatchEffects = getActions(effects, store.dispatch, {
      prevActions: cache.prevEffects,
      prevDispatchActions: cache.prevDispatchEffects,
    });
    cache.prevEffects = effects;
    cache.prevDispatchEffects = dispatchEffects;
    return dispatchEffects;
  })();

  return children({
    ...selectorProps,
    ...actionProps,
    ...effectProps,
    dispatch: store.dispatch,
  }) as JSX.Element;
};

const Connect = <A extends ActionTypes, E extends EffectTypes, S extends (...args: any) => any>({
  selector = (() => ({})) as S,
  actions = {} as A,
  effects = {} as E,
  ...other
}: Props<A, E, S>): JSX.Element => (
  <ReduxStoreContext.Consumer>
    {(context): JSX.Element => (
      <InnerConnect
        selector={selector}
        actions={actions}
        effects={effects}
        {...(context as StoreContextType)}
        {...other}
      />
    )}
  </ReduxStoreContext.Consumer>
);

export default Connect;

export type ContainerProps<
  C extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>,
  A = Record<string, unknown>,
  E = Record<string, unknown>,
  S extends (...args: any[]) => Record<string, unknown> = () => Record<string, unknown>
> = Omit<React.ComponentProps<C>, keyof A | keyof E | keyof ReturnType<S>>;

/*
Usage:
  type Props = ContainerProps<
    typeof MyComponent,
    typeof actions,
    typeof effects,
    typeof selector
  >;
  const MyComponentContainer = (props: Props): JSX.Element => (
  <Connect actions={actions} effects={effects} selector={selector}>
    {(connectedProps) => <MyComponent {...connectedProps} {...props} />}
  </Connect>
);
*/
